import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useDriver } from '../context/DriverContext';
import { useLanguage } from '../i18n/LanguageContext';
import {
  listenToMyIntertownRequests, respondToIntertownOffer, startIntertownTripWithOtp, updateIntertownTrip,
} from '../services/firebase';
import TripOtpInput from './TripOtpInput';

const liveStates = new Set(['awaiting_driver_acceptance', 'confirmed', 'arrived_at_pickup', 'started', 'at_destination', 'returning']);
const money = (value) => `₹${Math.round(Number(value) || 0).toLocaleString('en-IN')}`;
const STATUS_KEY_BY_STATE = Object.freeze({
  awaiting_driver_acceptance: 'offerStatus',
  confirmed: 'toPickupStatus',
  arrived_at_pickup: 'otpStatus',
  started: 'onTripStatus',
  at_destination: 'destinationStatus',
  returning: 'returnStatus',
});
const isFiniteCoordinate = (value) => value !== null && value !== undefined && value !== '' && Number.isFinite(Number(value));
const hasCurrentLocation = (location) => isFiniteCoordinate(location?.lat) && isFiniteCoordinate(location?.lng);

export default function IntertownTripCard() {
  const { user, myPos } = useDriver();
  const { t } = useLanguage();
  const [trips, setTrips] = useState([]);
  const [busy, setBusy] = useState(false);
  const [otp, setOtp] = useState('');
  useEffect(() => listenToMyIntertownRequests(user?.uid, setTrips), [user?.uid]);
  const trip = trips.find((item) => liveStates.has(item.status));
  useEffect(() => { setOtp(''); }, [trip?.id, trip?.status]);
  if (!trip) return null;
  const doAction = async (action, accept) => {
    setBusy(true);
    try {
      if (action === 'offer') await respondToIntertownOffer(trip.id, accept);
      else await updateIntertownTrip(trip.id, action, myPos);
      toast.success(accept === false ? t('intertown.offerDeclined') : t('intertown.tripUpdated'));
    } catch (error) { toast.error(error?.message || t('intertown.updateFailed')); }
    finally { setBusy(false); }
  };
  const startTrip = async (code) => {
    setBusy(true);
    try {
      await startIntertownTripWithOtp(trip.id, code);
    } catch (error) {
      setOtp('');
      toast.error(error?.message || t('intertown.updateFailed'));
    } finally {
      setBusy(false);
    }
  };
  const route = trip.route || {};
  const pickup = trip.pickup?.name || trip.pickup?.address || trip.pickupNote || t('intertown.pickupFallback');
  const statusKey = STATUS_KEY_BY_STATE[trip.status] || 'offerStatus';
  // Shared gate: arriving at pickup, arriving at destination, and completing
  // a return trip all require a GPS fix server-side now (each has its own
  // radius check in updateIntertownTripSecure), so all three buttons need
  // the same disabled-state + hint treatment rather than failing with only
  // a generic error toast after the tap.
  const canConfirmLocation = hasCurrentLocation(myPos);
  const customer = trip.customerName || t('intertown.passengerFallback');
  const distance = Number(trip.estimatedDistanceKm || route.distanceKm || 0);
  return (
    <section className="driver-intertown-card">
      <div className="driver-intertown-card-top">
        <div className="driver-intertown-eyebrow">{t('intertown.eyebrow')}</div>
        <span className={`driver-intertown-status status-${trip.status}`}>{t(`intertown.${statusKey}`)}</span>
      </div>
      <h3><span>{route.fromName || 'Pileru'}</span><span className="driver-intertown-route-arrow" aria-hidden>→</span><span>{route.toName || t('intertown.destinationFallback')}</span></h3>
      <div className="driver-intertown-metrics">
        <div><span>{t('intertown.tripLabel')}</span><strong>{trip.tripType === 'return' ? t('intertown.returnTrip') : t('intertown.oneWay')}</strong></div>
        <div><span>{t('intertown.distanceLabel')}</span><strong>{distance ? `${distance} km` : '—'}</strong></div>
        <div><span>{t('intertown.fareLabel')}</span><strong>{money(trip.totalFare || trip.baseFare)}</strong></div>
      </div>
      <div className="driver-intertown-pickup"><span className="driver-intertown-pickup-icon" aria-hidden>⌖</span><div><b>{t('intertown.pickupLabel')}</b><span>{pickup}</span></div></div>
      <div className="driver-intertown-passenger"><span aria-hidden>👤</span><span><b>{t('intertown.passengerLabel')}</b> {customer}</span></div>
      {trip.status === 'awaiting_driver_acceptance' && <div className="driver-intertown-actions"><button type="button" className="btn btn-ghost" disabled={busy} onClick={() => doAction('offer', false)}>{t('intertown.decline')}</button><button type="button" className="btn btn-green" disabled={busy} onClick={() => doAction('offer', true)}>{busy ? t('intertown.updating') : t('intertown.accept')}</button></div>}
      {trip.status === 'confirmed' && (
        <div className="driver-intertown-actions">
          <button type="button" className="btn btn-ghost" disabled={busy} onClick={() => {
            const lat = Number(trip.pickup?.lat); const lng = Number(trip.pickup?.lng);
            if (Number.isFinite(lat) && Number.isFinite(lng)) window.open(`https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}&travelmode=driving`, '_blank', 'noopener,noreferrer');
          }}>{t('intertown.navigatePickup')}</button>
          <button type="button" className="btn btn-green" disabled={busy || !canConfirmLocation} onClick={() => doAction('arrived_at_pickup')}>{busy ? t('intertown.updating') : t('intertown.arrivedAtPickup')}</button>
        </div>
      )}
      {trip.status === 'confirmed' && !canConfirmLocation && <p className="driver-intertown-location-note" role="status">{t('intertown.waitingForLocation')}</p>}
      {trip.status === 'arrived_at_pickup' && (
        <div className="driver-intertown-actions driver-intertown-otp">
          <TripOtpInput
            value={otp}
            onChange={setOtp}
            onComplete={startTrip}
            disabled={busy}
            hint={t('intertown.otpHint')}
          />
          <button type="button" className="btn btn-green" disabled={busy || otp.length < 4} onClick={() => startTrip(otp)}>
            {busy ? t('intertown.updating') : t('intertown.startTrip')}
          </button>
        </div>
      )}
      {trip.status === 'started' && (
        <>
          <div className="driver-intertown-actions">{Number.isFinite(Number(trip.destination?.lat)) && Number.isFinite(Number(trip.destination?.lng)) && <button type="button" className="btn btn-ghost" disabled={busy} onClick={() => {
            const lat = Number(trip.destination.lat); const lng = Number(trip.destination.lng);
            window.open(`https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}&travelmode=driving`, '_blank', 'noopener,noreferrer');
          }}>{t('intertown.navigateDestination')}</button>}<button type="button" className="btn btn-green" disabled={busy || !canConfirmLocation} onClick={() => doAction('arrived')}>{busy ? t('intertown.updating') : t('intertown.reachedDestination')}</button></div>
          {!canConfirmLocation && <p className="driver-intertown-location-note" role="status">{t('intertown.waitingForLocation')}</p>}
        </>
      )}
      {trip.status === 'at_destination' && <div className="driver-intertown-actions"><span>{t('intertown.waitingStarted')}</span><button type="button" className="btn btn-green" disabled={busy} onClick={() => doAction('start_return')}>{busy ? t('intertown.updating') : t('intertown.startReturn')}</button></div>}
      {trip.status === 'returning' && (
        <>
          <div className="driver-intertown-actions"><span>{t('intertown.finalFare', { amount: money(trip.totalFare) })}</span><button type="button" className="btn btn-green" disabled={busy || !canConfirmLocation} onClick={() => doAction('complete')}>{busy ? t('intertown.updating') : t('intertown.completeTrip')}</button></div>
          {!canConfirmLocation && <p className="driver-intertown-location-note" role="status">{t('intertown.waitingForLocation')}</p>}
        </>
      )}
    </section>
  );
}
